import { Container } from '@cleartrip/ct-design-components'
import React from 'react'

import { PE_HEADER_PATTERN_CONFIG } from '@/constants/flights/premiumEconomy'
import { CabinInterstitialTheme } from '@/types/srp'

type HeaderCornerPatternProps = {
    variant?: CabinInterstitialTheme['headerPatternVariant']
}

/** Quote CDN urls — Rukmini `f_auto,q_auto` commas break unquoted CSS `url()`. */
const cssUrl = (src: string) => `url("${src}")`

/**
 * Right-corner decorative pattern for themed interstitial headers.
 *
 * Premium Economy (Figma 512:11337 / 4094:50333): CDN `pe-header-pattern.png` as
 * background-image in the Figma layer box (686×592, top -302, mask-size 462×653).
 * Painted as background-image — CSS mask-image can't use cross-origin CDN assets.
 *
 * First Class: same CDN PNG as flights-pwa, but stretched to ~mid-header with a
 * leftward fade (fixed 451px only covered ~20% of the desktop modal).
 */
const HeaderCornerPattern: React.FC<HeaderCornerPatternProps> = ({ variant = 'premium' }) => {
    const config = PE_HEADER_PATTERN_CONFIG[variant]
    const {
        src,
        opacity,
        layerWidthPx = 686,
        layerWidthPercent,
        layerHeightPx = 592,
        layerOffsetRightPx = -18,
        layerOffsetTopPx = -302,
        backgroundSize = '462px 653px',
        backgroundPosition = '223px 0',
        fade,
    } = config

    const isPercentLayer = typeof layerWidthPercent === 'number'

    return (
        <Container
            aria-hidden
            className="p-absolute t-0 r-0 b-0 l-0 o-hidden disable-pointer-events"
            css={{ zIndex: 0 }}
        >
            <Container
                css={{
                    position: 'absolute',
                    right: `${layerOffsetRightPx}px`,
                    top: isPercentLayer ? 0 : `${layerOffsetTopPx}px`,
                    bottom: isPercentLayer ? 0 : undefined,
                    width: isPercentLayer ? `${layerWidthPercent}%` : `${layerWidthPx}px`,
                    height: isPercentLayer ? undefined : `${layerHeightPx}px`,
                    backgroundImage: cssUrl(src),
                    backgroundRepeat: 'no-repeat',
                    backgroundSize,
                    backgroundPosition,
                    opacity,
                    // Gradient mask only — dissolves the motif (no CDN in mask-image).
                    ...(fade
                        ? {
                              WebkitMaskImage: fade,
                              maskImage: fade,
                              WebkitMaskSize: '100% 100%',
                              maskSize: '100% 100%',
                              WebkitMaskRepeat: 'no-repeat',
                              maskRepeat: 'no-repeat',
                          }
                        : {}),
                    pointerEvents: 'none',
                }}
            />
        </Container>
    )
}

export default HeaderCornerPattern
